import { useEffect, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import {
  DesktopDialog,
  HubSheet,
  HubSheetActions,
} from "../AdminPortal/HubPwa";
import aa from "../AdminPortal/adminApp.module.css";
import { Field, Input, Select, useToast } from "../../ui";
import { toDateInputValue } from "../../../utils/appointmentLabels";

function amountFromAttendance(attendance) {
  if (
    attendance?.session_fee_amount != null &&
    Number(attendance.session_fee_amount) > 0
  ) {
    return String(Math.round(Number(attendance.session_fee_amount)));
  }
  return "";
}

function formatFeeRub(attendance) {
  const n = Number(attendance?.session_fee_amount);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n).toLocaleString("ru-RU");
}

/**
 * Кнопка в журнале + окно оплаты разового занятия.
 */
export default function AcceptSessionFeePayment({ attendance, onSuccess }) {
  const toast = useToast();
  const isMobile = useIsMobile(768);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState("cash");
  const [amount, setAmount] = useState(() => amountFromAttendance(attendance));
  const [paidDate, setPaidDate] = useState(() => toDateInputValue());

  useEffect(() => {
    if (!open) return;
    setAmount(amountFromAttendance(attendance));
    setMethod("cash");
    setPaidDate(toDateInputValue());
  }, [open, attendance?.id, attendance?.session_fee_amount]);

  if (!attendance || attendance.session_fee_status !== "due") return null;

  const feeLabel = formatFeeRub(attendance);
  const name = attendance.patient?.full_name || "Ученик";

  const parseSum = () => {
    const sum = Number(String(amount).replace(",", "."));
    if (!Number.isFinite(sum) || sum <= 0) {
      toast.error("Укажите сумму за это занятие");
      return null;
    }
    return sum;
  };

  const close = () => {
    if (!busy) setOpen(false);
  };

  const saveAmount = async () => {
    const sum = parseSum();
    if (sum == null) return;
    setBusy(true);
    try {
      const data = await clinicAPI.updateSessionFeeAmount({
        attendance_id: attendance.id,
        amount: sum,
      });
      toast.success("Сумма сохранена — клиент может оплатить онлайн");
      setOpen(false);
      onSuccess?.(data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось сохранить сумму");
    } finally {
      setBusy(false);
    }
  };

  const acceptPay = async (e) => {
    e?.preventDefault?.();
    const sum = parseSum();
    if (sum == null) return;
    if (!paidDate) {
      toast.error("Укажите дату оплаты");
      return;
    }
    const paidAt = new Date(`${paidDate}T12:00:00`);
    if (Number.isNaN(paidAt.getTime())) {
      toast.error("Некорректная дата оплаты");
      return;
    }
    setBusy(true);
    try {
      const data = await clinicAPI.createSessionFeePayment({
        attendance_id: attendance.id,
        amount: sum,
        method,
        paid_at: paidAt.toISOString(),
      });
      toast.success("Оплата за занятие принята");
      setOpen(false);
      onSuccess?.(data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось записать оплату");
    } finally {
      setBusy(false);
    }
  };

  const Sheet = isMobile ? HubSheet : DesktopDialog;
  const formId = "journal-session-fee-form";

  return (
    <>
      <button
        type="button"
        className={aa.journalSessionFeeChip}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <span className={aa.journalSessionFeeChipLabel}>
          {feeLabel ? `К оплате ${feeLabel} ₽` : "Указать сумму"}
        </span>
        <span className={aa.journalSessionFeeChipAction}>Оплатить</span>
      </button>

      <Sheet
        open={open}
        onClose={close}
        title="Оплата за занятие"
        subtitle={name}
        titleId="journal-session-fee-title"
        bodyClassName={aa.journalSessionFeeSheetBody}
        sheetClassName={aa.journalSessionFeeDialog}
        dialogClassName={aa.journalSessionFeeDialog}
        footer={
          <HubSheetActions
            primary={busy ? "…" : "Принять оплату"}
            primaryForm={formId}
            primaryType="submit"
            primaryDisabled={busy}
            secondaryLabel="Сохранить для онлайн"
            onSecondary={() => {
              if (!busy) saveAmount();
            }}
          />
        }
      >
        <form id={formId} className={aa.journalSessionFeeForm} onSubmit={acceptPay}>
          <p className={aa.journalSessionFeeLead}>
            Разовое посещение. Сумма из настроек группы или клуба — можно
            изменить.
          </p>
          <Field label="Сумма, ₽">
            <Input
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Например 900"
              required
              autoFocus
              disabled={busy}
            />
          </Field>
          <Field label="Способ оплаты">
            <Select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              disabled={busy}
            >
              <option value="cash">Наличные</option>
              <option value="card_transfer">Перевод</option>
              <option value="other">Другое</option>
            </Select>
          </Field>
          <Field label="Дата оплаты">
            <Input
              type="date"
              value={paidDate}
              max={toDateInputValue()}
              onChange={(e) => setPaidDate(e.target.value)}
              disabled={busy}
              required
            />
          </Field>
        </form>
      </Sheet>
    </>
  );
}
